<template>
  <q-page padding>
    <div class="row items-center q-mb-lg">
      <div class="text-h4">Admin Dashboard</div>
      <q-space />
      <q-badge color="primary">Administrator</q-badge>
    </div>

    <q-tabs v-model="activeTab" dense class="text-grey q-mb-lg">
      <q-tab name="organizations" label="Organizations" icon="business" />
      <q-tab name="email_config" label="Email Configuration" icon="mail" />
      <q-tab name="users" label="Users" icon="people" />
    </q-tabs>

    <!-- ================ ORGANIZATIONS TAB ================ -->
    <q-tab-panels v-model="activeTab">
      <q-tab-panel name="organizations">
        <div class="text-subtitle1 q-mb-md">Manage Organizations</div>

        <!-- Create Organization Button -->
        <q-btn
          color="primary"
          label="Create Organization"
          icon="add"
          @click="showCreateOrgDialog = true"
          class="q-mb-lg"
        />

        <!-- Organizations List -->
        <q-table
          :rows="organizations"
          :columns="orgColumns"
          row-key="id"
          card-class="rounded-borders"
          flat
          bordered
          :loading="loadingOrgs"
        >
          <template v-slot:body-cell-actions="props">
            <q-td :props="props">
              <q-btn
                flat
                dense
                size="sm"
                color="primary"
                icon="edit"
                @click="editOrganization(props.row)"
              >
                <q-tooltip>Edit</q-tooltip>
              </q-btn>
              <q-btn
                flat
                dense
                size="sm"
                color="negative"
                icon="delete"
                @click="deleteOrganization(props.row)"
              >
                <q-tooltip>Delete</q-tooltip>
              </q-btn>
            </q-td>
          </template>
        </q-table>

        <!-- Create/Edit Organization Dialog -->
        <q-dialog v-model="showCreateOrgDialog">
          <q-card style="min-width: 500px">
            <q-card-section class="row items-center q-pb-none">
              <div class="text-h6">
                {{ editingOrg ? 'Edit Organization' : 'Create Organization' }}
              </div>
              <q-space />
              <q-btn icon="close" flat round dense v-close-popup />
            </q-card-section>

            <q-card-section>
              <q-form @submit="saveOrganization">
                <q-input
                  v-model="orgForm.name"
                  label="Organization Name *"
                  outlined
                  dense
                  class="q-mb-md"
                  :rules="[(val) => !!val || 'Name is required']"
                />

                <q-input
                  v-model="orgForm.domain"
                  label="Domain *"
                  outlined
                  dense
                  class="q-mb-md"
                  :rules="[(val) => !!val || 'Domain is required']"
                  hint="e.g., kebenei-softsmarttech.de"
                />

                <div class="row q-gutter-sm">
                  <q-btn
                    type="submit"
                    color="primary"
                    label="Save"
                    :loading="savingOrg"
                  />
                  <q-btn label="Cancel" color="grey-8" flat v-close-popup />
                </div>
              </q-form>
            </q-card-section>
          </q-card>
        </q-dialog>
      </q-tab-panel>

      <!-- ================ EMAIL CONFIG TAB ================ -->
      <q-tab-panel name="email_config">
        <div class="text-subtitle1 q-mb-md">
          Email Configuration by Organization
        </div>

        <!-- Select Organization -->
        <q-select
          v-model="selectedOrgForEmail"
          :options="organizations"
          option-value="id"
          option-label="name"
          label="Select Organization"
          outlined
          dense
          class="q-mb-lg"
          style="max-width: 300px"
          @update:model-value="loadEmailConfig"
        />

        <div v-if="selectedOrgForEmail">
          <q-btn
            color="primary"
            label="Configure Email"
            icon="settings"
            @click="showEmailConfigDialog = true"
            class="q-mb-lg"
          />

          <!-- Current Email Config Display -->
          <q-card v-if="currentEmailConfig" class="bg-grey-1 q-mb-lg">
            <q-card-section>
              <div class="text-subtitle2 q-mb-md">Current Configuration</div>
              <div class="row q-col-gutter-md">
                <div class="col-12 col-sm-6">
                  <div class="text-caption text-grey-7">Provider</div>
                  <div class="text-body2">
                    {{ currentEmailConfig.provider }}
                  </div>
                </div>
                <div class="col-12 col-sm-6">
                  <div class="text-caption text-grey-7">From Email</div>
                  <div class="text-body2">
                    {{ currentEmailConfig.from_email }}
                  </div>
                </div>
                <div class="col-12 col-sm-6">
                  <div class="text-caption text-grey-7">Username</div>
                  <div class="text-body2">
                    {{ currentEmailConfig.username }}
                  </div>
                </div>
                <div class="col-12 col-sm-6">
                  <div class="text-caption text-grey-7">Status</div>
                  <q-badge
                    :color="
                      currentEmailConfig.is_active ? 'positive' : 'negative'
                    "
                  >
                    {{ currentEmailConfig.is_active ? 'Active' : 'Inactive' }}
                  </q-badge>
                </div>
              </div>
              <div class="q-mt-md">
                <q-btn
                  color="info"
                  label="Test Connection"
                  icon="mail_outline"
                  outline
                  @click="testEmailConnection"
                  :loading="testingConnection"
                />
              </div>
            </q-card-section>
          </q-card>

          <div v-else class="text-grey-7 q-pa-md bg-grey-1 rounded-borders">
            No email configuration found. Click "Configure Email" to set one up.
          </div>

          <!-- Email Config Dialog -->
          <q-dialog v-model="showEmailConfigDialog">
            <q-card style="min-width: 600px">
              <q-card-section class="row items-center q-pb-none">
                <div class="text-h6">Configure Email Provider</div>
                <q-space />
                <q-btn icon="close" flat round dense v-close-popup />
              </q-card-section>

              <q-card-section>
                <q-form @submit="saveEmailConfig">
                  <!-- Provider Selection -->
                  <div class="text-subtitle2 q-mb-md">Email Provider</div>
                  <q-option-group
                    v-model="emailConfigForm.provider"
                    :options="providerOptions"
                    color="primary"
                    inline
                    class="q-mb-lg"
                  />

                  <!-- Common Fields -->
                  <q-input
                    v-model="emailConfigForm.from_email"
                    label="From Email (Sender Address) *"
                    outlined
                    dense
                    class="q-mb-md"
                    :rules="[(val) => !!val || 'Email is required']"
                  />

                  <q-input
                    v-model="emailConfigForm.username"
                    label="Username/Email *"
                    outlined
                    dense
                    class="q-mb-md"
                    :rules="[(val) => !!val || 'Username is required']"
                    :hint="getUsernameHint()"
                  />

                  <q-input
                    v-model="emailConfigForm.password"
                    label="Password/App Password *"
                    outlined
                    dense
                    type="password"
                    class="q-mb-md"
                    :rules="[(val) => !!val || 'Password is required']"
                    :hint="getPasswordHint()"
                  />

                  <!-- Custom SMTP Fields -->
                  <div
                    v-if="emailConfigForm.provider === 'custom'"
                    class="q-mb-md"
                  >
                    <q-input
                      v-model="emailConfigForm.smtp_host"
                      label="SMTP Host *"
                      outlined
                      dense
                      class="q-mb-md"
                      hint="e.g., mail.yourdomain.com"
                      :rules="[(val) => !!val || 'SMTP Host is required']"
                    />

                    <q-input
                      v-model.number="emailConfigForm.smtp_port"
                      label="SMTP Port *"
                      outlined
                      dense
                      type="number"
                      class="q-mb-md"
                      hint="Usually 587 (TLS) or 465 (SSL)"
                      :rules="[(val) => !!val || 'SMTP Port is required']"
                    />

                    <q-checkbox
                      v-model="emailConfigForm.use_tls"
                      label="Use TLS"
                      class="q-mb-md"
                    />
                  </div>

                  <!-- Active Toggle -->
                  <q-checkbox
                    v-model="emailConfigForm.is_active"
                    label="Activate this configuration"
                    class="q-mb-lg"
                  />

                  <!-- Instructions -->
                  <q-banner class="bg-info text-white q-mb-lg" dense>
                    <template v-slot:avatar>
                      <q-icon name="info" />
                    </template>
                    {{ getProviderInstructions() }}
                  </q-banner>

                  <div class="row q-gutter-sm">
                    <q-btn
                      type="submit"
                      color="primary"
                      label="Save Configuration"
                      :loading="savingEmailConfig"
                    />
                    <q-btn label="Cancel" color="grey-8" flat v-close-popup />
                  </div>
                </q-form>
              </q-card-section>
            </q-card>
          </q-dialog>
        </div>
        <div v-else class="text-grey-7">
          Please select an organization to configure email settings.
        </div>
      </q-tab-panel>

      <!-- ================ USERS TAB ================ -->
      <q-tab-panel name="users">
        <div class="text-subtitle1 q-mb-md">User Management</div>
        <q-btn
          color="primary"
          label="View Users"
          @click="navigateToUserManagement"
          class="q-mb-lg"
        />
        <p class="text-grey-7">
          User management is available in the main User Management page.
        </p>
      </q-tab-panel>
    </q-tab-panels>
  </q-page>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useQuasar } from 'quasar';
import { useRouter } from 'vue-router';
import { api } from '../api';

const $q = useQuasar();
const router = useRouter();

/* -------- State -------- */
const activeTab = ref('organizations');
const organizations = ref([]);
const currentEmailConfig = ref(null);
const loadingOrgs = ref(false);
const savingOrg = ref(false);
const savingEmailConfig = ref(false);
const testingConnection = ref(false);

/* Dialog states */
const showCreateOrgDialog = ref(false);
const showEmailConfigDialog = ref(false);

/* Form states */
const orgForm = ref({ name: '', domain: '' });
const editingOrg = ref(null);
const selectedOrgForEmail = ref(null);
const emailConfigForm = ref({
  provider: 'gmail',
  from_email: '',
  username: '',
  password: '',
  smtp_host: '',
  smtp_port: 587,
  use_tls: true,
  is_active: true,
});

/* Table columns */
const orgColumns = [
  { name: 'id', label: 'ID', field: 'id', align: 'left' },
  { name: 'name', label: 'Organization Name', field: 'name', align: 'left' },
  { name: 'domain', label: 'Domain', field: 'domain', align: 'left' },
  { name: 'created_at', label: 'Created', field: 'created_at', align: 'left' },
  { name: 'actions', label: 'Actions', field: 'actions', align: 'center' },
];

/* Options */
const providerOptions = [
  { label: 'Gmail', value: 'gmail' },
  { label: 'Outlook (Office365)', value: 'outlook' },
  { label: 'Yahoo', value: 'yahoo' },
  { label: 'Custom SMTP', value: 'custom' },
];

/* -------- Methods -------- */
function notify(type, message) {
  $q.notify({ type, message, position: 'top-right' });
}

function getUsernameHint() {
  const hints = {
    gmail: 'Your Gmail address (e.g., user@gmail.com)',
    outlook: 'Your Outlook/Office365 email',
    yahoo: 'Your Yahoo email',
    custom: 'SMTP username from your mail server',
  };
  return hints[emailConfigForm.value.provider] || '';
}

function getPasswordHint() {
  const hints = {
    gmail: 'Use Gmail App Password (NOT your regular password)',
    outlook: 'Your Outlook password or App Password',
    yahoo: 'Your Yahoo password',
    custom: 'SMTP password from your mail server',
  };
  return hints[emailConfigForm.value.provider] || '';
}

function getProviderInstructions() {
  const instructions = {
    gmail:
      '📌 For Gmail: Go to myaccount.google.com/apppasswords → Create App Password → Copy 16-char password',
    outlook:
      '📌 For Outlook: Use your Microsoft account password or generate an App Password',
    yahoo: '📌 For Yahoo: Use your Yahoo account password',
    custom:
      '📌 For Custom: Contact your mail server administrator for SMTP credentials',
  };
  return instructions[emailConfigForm.value.provider] || '';
}

async function loadOrganizations() {
  loadingOrgs.value = true;
  try {
    const { data } = await api.get('organizations/');
    organizations.value = data.results || data;
  } catch (err) {
    console.error(err);
    notify('negative', 'Failed to load organizations');
  } finally {
    loadingOrgs.value = false;
  }
}

async function saveOrganization() {
  savingOrg.value = true;
  try {
    if (editingOrg.value) {
      // Update
      await api.put(
        `organizations/${editingOrg.value.id}/`,
        orgForm.value,
      );
      notify('positive', 'Organization updated successfully');
    } else {
      // Create
      await api.post('organizations/', orgForm.value);
      notify('positive', 'Organization created successfully');
    }
    showCreateOrgDialog.value = false;
    orgForm.value = { name: '', domain: '' };
    editingOrg.value = null;
    await loadOrganizations();
  } catch (err) {
    console.error(err);
    notify(
      'negative',
      err.response?.data?.detail || 'Failed to save organization',
    );
  } finally {
    savingOrg.value = false;
  }
}

function editOrganization(org) {
  editingOrg.value = org;
  orgForm.value = { name: org.name, domain: org.domain };
  showCreateOrgDialog.value = true;
}

async function deleteOrganization(org) {
  $q.dialog({
    title: 'Delete Organization',
    message: `Are you sure you want to delete "${org.name}"?`,
    cancel: true,
    persistent: true,
  }).onOk(async () => {
    try {
      await api.delete(`organizations/${org.id}/`);
      notify('positive', 'Organization deleted');
      await loadOrganizations();
    } catch (err) {
      console.error(err);
      notify('negative', 'Failed to delete organization');
    }
  });
}

async function loadEmailConfig() {
  if (!selectedOrgForEmail.value) {
    currentEmailConfig.value = null;
    return;
  }

  try {
    const { data } = await api.get(
      `organizations/${selectedOrgForEmail.value}/email_config/`,
    );
    currentEmailConfig.value = data;
    emailConfigForm.value = {
      provider: data.provider,
      from_email: data.from_email,
      username: data.username,
      password: '', // Never pre-fill password
      smtp_host: data.smtp_host || '',
      smtp_port: data.smtp_port || 587,
      use_tls: data.use_tls || true,
      is_active: data.is_active,
    };
  } catch (err) {
    currentEmailConfig.value = null;
    emailConfigForm.value = {
      provider: 'gmail',
      from_email: '',
      username: '',
      password: '',
      smtp_host: '',
      smtp_port: 587,
      use_tls: true,
      is_active: true,
    };
  }
}

async function saveEmailConfig() {
  if (!selectedOrgForEmail.value) {
    notify('negative', 'Select an organization first');
    return;
  }

  savingEmailConfig.value = true;
  try {
    const payload = {
      organization: selectedOrgForEmail.value,
      ...emailConfigForm.value,
    };

    if (currentEmailConfig.value) {
      // Update
      await api.put(
        `organizations/${selectedOrgForEmail.value}/email_config/`,
        payload,
      );
      notify('positive', 'Email configuration updated');
    } else {
      // Create
      await api.post(
        `organizations/${selectedOrgForEmail.value}/email_config/`,
        payload,
      );
      notify('positive', 'Email configuration created');
    }

    showEmailConfigDialog.value = false;
    await loadEmailConfig();
  } catch (err) {
    console.error(err);
    notify(
      'negative',
      err.response?.data?.detail || 'Failed to save configuration',
    );
  } finally {
    savingEmailConfig.value = false;
  }
}

async function testEmailConnection() {
  if (!currentEmailConfig.value) {
    notify('negative', 'No email configuration found');
    return;
  }

  testingConnection.value = true;
  try {
    await api.post(
      `email-configs/${currentEmailConfig.value.id}/test_connection/`,
      { test_email: currentEmailConfig.value.from_email },
    );
    notify(
      'positive',
      `Test email sent to ${currentEmailConfig.value.from_email}. Check your inbox!`,
    );
  } catch (err) {
    console.error(err);
    notify(
      'negative',
      err.response?.data?.detail || 'Failed to send test email',
    );
  } finally {
    testingConnection.value = false;
  }
}

function navigateToUserManagement() {
  router.push('iam/users');
}

onMounted(() => {
  loadOrganizations();
});
</script>

<style scoped>
.rounded-borders {
  border-radius: 4px;
}
</style>
